import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import {
  AdminTable, AdminSectionHeader, tdCls, exportCsv, StatCard,
} from '@/components/admin/AdminShared';

const INCOME_PERIODS = [
  { key: 'day',     label: 'Today' },
  { key: 'week',    label: 'This week' },
  { key: 'month',   label: 'This month' },
  { key: '3months', label: 'Last 3 months' },
] as const;
type IncomePeriod = (typeof INCOME_PERIODS)[number]['key'];

export default function AdminAnalytics() {
  const [incomePeriod, setIncomePeriod] = useState<IncomePeriod>('month');
  const income            = trpc.analytics.income.useQuery({ period: incomePeriod });
  const overview          = trpc.analytics.overview.useQuery();
  const purchasesByCourse = trpc.analytics.purchasesByCourse.useQuery();
  const basketByCourse    = trpc.analytics.basketByCourse.useQuery();
  const recentPurchases   = trpc.analytics.recentPurchases.useQuery();
  const videoStats        = trpc.analytics.videoViews.useQuery();
  const repurchases       = trpc.analytics.repurchasesByCourse.useQuery();
  const expiringSoon      = trpc.analytics.expiringSoon.useQuery();
  const accessBuckets     = trpc.analytics.accessRemainingBuckets.useQuery();

  const o = overview.data;
  const byCourse = purchasesByCourse.data ?? [];
  const baskets  = basketByCourse.data    ?? [];
  const recent   = recentPurchases.data   ?? [];
  const videos   = videoStats.data        ?? [];
  const repurch  = repurchases.data       ?? [];
  const expiring = expiringSoon.data      ?? [];
  const buckets  = accessBuckets.data;

  /** Quick lookup: courseId → number of users with it in their basket */
  const basketMap = new Map(baskets.map(b => [b.courseId, b.inBasket]));

  /** Courses that are ONLY in baskets (not purchased) — surface them separately. */
  const purchasedCourseIds = new Set(byCourse.map(r => r.courseId));
  const basketsNotPurchased = baskets.filter(b => !purchasedCourseIds.has(b.courseId));

  return (
    <div className="space-y-10">
      <AdminSectionHeader title="Analytics" />

      {/* ── User counts ───────────────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-bold text-[#71717A] uppercase tracking-widest mb-3">
          Users
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="All time"   value={o?.users.total       ?? 0} />
          <StatCard label="Last 7 d"   value={o?.users.last7Days   ?? 0} />
          <StatCard label="Last 30 d"  value={o?.users.last30Days  ?? 0} />
          <StatCard label="Last 90 d"  value={o?.users.last90Days  ?? 0} />
          <StatCard label="Last 365 d" value={o?.users.last365Days ?? 0} />
        </div>
      </section>

      {/* ── Sales summary ─────────────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-bold text-[#71717A] uppercase tracking-widest mb-3">
          Sales (completed)
        </h3>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <StatCard label="Total orders" value={o?.purchases.total ?? 0} />
          <StatCard label="Revenue (KD)" value={(o?.purchases.revenue ?? 0).toFixed(2)} />
        </div>
      </section>

      {/* ── Income filter (day / week / month / 3 months) ─────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <h3 className="text-sm font-bold text-[#71717A] uppercase tracking-widest">
            Income
          </h3>
          <div className="flex flex-wrap gap-2">
            {INCOME_PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => setIncomePeriod(p.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors border
                            ${incomePeriod === p.key
                              ? 'bg-yellow-400 text-black border-yellow-400'
                              : 'bg-[#18181B] text-[#A1A1AA] border-white/10 hover:border-white/30'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-2xl">
          <StatCard label="Total income (KD)"   value={(income.data?.total         ?? 0).toFixed(2)} />
          <StatCard label="Course sales (KD)"   value={(income.data?.courseIncome  ?? 0).toFixed(2)} />
          <StatCard label="Online sessions (KD)" value={(income.data?.sessionIncome ?? 0).toFixed(2)} />
        </div>
        {income.data && income.data.scope === 'mine' && (
          <p className="text-[#52525B] text-xs mt-2">
            Showing your share — {income.data.coursePercent}% of course sales, {income.data.sessionPercent}% of sessions.
          </p>
        )}
      </section>

      {/* ── Purchases per course ──────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#71717A] uppercase tracking-widest">
            Purchases by course
          </h3>
          <button
            onClick={() => exportCsv(byCourse, 'mrpanadol_purchases_by_course')}
            className="text-xs text-[#A1A1AA] hover:text-yellow-400 transition-colors"
          >Export CSV</button>
        </div>
        <AdminTable
          headers={['Course', 'Purchased', 'In basket', '7d', '30d', '90d', '365d', 'Revenue (KD)']}
          empty={byCourse.length === 0}
        >
          {byCourse.map(r => (
            <tr key={r.courseId} className="border-t border-white/[0.04]">
              <td className={`${tdCls} text-white text-sm font-semibold`}>{r.title}</td>
              <td className={`${tdCls} text-[#E4E4E7] text-sm`}>{r.total}</td>
              <td className={`${tdCls} text-yellow-400 text-sm font-bold`}>
                {basketMap.get(r.courseId) ?? 0}
              </td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>{r.last7Days}</td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>{r.last30Days}</td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>{r.last90Days}</td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>{r.last365Days}</td>
              <td className={`${tdCls} text-yellow-400 font-bold text-sm`}>{r.revenue.toFixed(2)}</td>
            </tr>
          ))}
        </AdminTable>

        {/* Courses sitting in someone's basket but never purchased by anyone yet */}
        {basketsNotPurchased.length > 0 && (
          <div className="mt-6">
            <h4 className="text-xs font-bold text-[#71717A] uppercase tracking-widest mb-2">
              In basket only (no purchases yet)
            </h4>
            <AdminTable
              headers={['Course', 'In basket']}
              empty={false}
            >
              {basketsNotPurchased.map(b => (
                <tr key={b.courseId} className="border-t border-white/[0.04]">
                  <td className={`${tdCls} text-white text-sm font-semibold`}>{b.title}</td>
                  <td className={`${tdCls} text-yellow-400 text-sm font-bold`}>{b.inBasket}</td>
                </tr>
              ))}
            </AdminTable>
          </div>
        )}
      </section>

      {/* ── Recent purchases (timing) ────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#71717A] uppercase tracking-widest">
            Recent purchases ({recent.length})
          </h3>
          <button
            onClick={() =>
              exportCsv(
                recent.map(p => ({
                  ID:        p.id,
                  When:      new Date(p.purchaseDate).toISOString(),
                  Buyer:     p.user.name,
                  Phone:     p.user.phoneNumber,
                  Course:    p.course.title,
                  Price_KD:  p.pricePaid,
                  Status:    p.status,
                })),
                'mrpanadol_recent_purchases',
              )
            }
            className="text-xs text-[#A1A1AA] hover:text-yellow-400 transition-colors"
          >Export CSV</button>
        </div>
        <AdminTable
          headers={['When', 'Buyer', 'Phone', 'Course', 'Price', 'Status']}
          empty={recent.length === 0}
        >
          {recent.map(p => (
            <tr key={p.id} className="border-t border-white/[0.04]">
              <td className={`${tdCls} text-[#A1A1AA] text-xs whitespace-nowrap`}>
                {new Date(p.purchaseDate).toLocaleString()}
              </td>
              <td className={`${tdCls} text-white text-sm`}>{p.user.name}</td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>{p.user.phoneNumber}</td>
              <td className={`${tdCls} text-[#E4E4E7] text-sm`}>{p.course.title}</td>
              <td className={`${tdCls} text-yellow-400 text-sm font-bold`}>
                {parseFloat(p.pricePaid as any) === 0 ? 'Free' : `${p.pricePaid} KD`}
              </td>
              <td className={`${tdCls} text-[#A1A1AA] text-xs`}>{p.status}</td>
            </tr>
          ))}
        </AdminTable>
      </section>

      {/* ── Re-purchases by course ─────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#71717A] uppercase tracking-widest">
            Re-purchases by course
          </h3>
          <button
            onClick={() => exportCsv(repurch as any, 'mrpanadol_repurchases')}
            className="text-xs text-[#A1A1AA] hover:text-yellow-400 transition-colors"
          >Export CSV</button>
        </div>
        <AdminTable
          headers={['Course', 'Total events', 'Distinct buyers', 'Re-purchases', 'Re-purchase revenue', 'Latest']}
          empty={repurch.length === 0}
        >
          {repurch.map(r => (
            <tr key={r.courseId} className="border-t border-white/[0.04]">
              <td className={`${tdCls} text-white text-sm font-semibold`}>{r.title}</td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>{r.totalEvents}</td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>{r.distinctBuyers}</td>
              <td className={`${tdCls} text-yellow-400 text-sm font-bold`}>{r.repurchaseCount}</td>
              <td className={`${tdCls} text-yellow-400 text-sm`}>
                {r.repurchaseRev > 0 ? `${r.repurchaseRev.toFixed(2)} KD` : '—'}
              </td>
              <td className={`${tdCls} text-[#52525B] text-xs whitespace-nowrap`}>
                {r.latestAt ? new Date(r.latestAt as any).toLocaleDateString() : '—'}
              </td>
            </tr>
          ))}
        </AdminTable>
      </section>

      {/* ── Access remaining (active purchases) ────────────────────────── */}
      <section>
        <h3 className="text-sm font-bold text-[#71717A] uppercase tracking-widest mb-3">
          Time left on active access
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total active" value={buckets?.total    ?? 0} />
          <StatCard label="≤ 30 days"    value={buckets?.under30  ?? 0} color="#f87171" />
          <StatCard label="30 – 90 d"    value={buckets?.under90  ?? 0} color="#fbbf24" />
          <StatCard label="90 – 180 d"   value={buckets?.under180 ?? 0} />
          <StatCard label="180 – 365 d"  value={buckets?.under365 ?? 0} color="#4ade80" />
        </div>
        {(buckets?.noExpiry ?? 0) > 0 && (
          <p className="text-[#52525B] text-xs mt-2">
            {buckets?.noExpiry} legacy purchase{buckets?.noExpiry === 1 ? '' : 's'} with no expiry on file (treated as active).
          </p>
        )}
      </section>

      {/* ── Expiring within 30 days ────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#71717A] uppercase tracking-widest">
            Expiring within 30 days
          </h3>
          <button
            onClick={() => exportCsv(expiring as any, 'mrpanadol_expiring_soon')}
            className="text-xs text-[#A1A1AA] hover:text-yellow-400 transition-colors"
          >Export CSV</button>
        </div>
        <AdminTable
          headers={['Buyer', 'Phone', 'Course', 'Days left', 'Expires on']}
          empty={expiring.length === 0}
        >
          {expiring.map(e => (
            <tr key={e.id} className="border-t border-white/[0.04]">
              <td className={`${tdCls} text-white text-sm`}>{e.userName ?? '—'}</td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>{e.phone ?? '—'}</td>
              <td className={`${tdCls} text-[#E4E4E7] text-sm`}>{e.courseTitle ?? '—'}</td>
              <td className={`${tdCls} text-sm font-bold ${(e.daysLeft ?? 0) <= 7 ? 'text-red-400' : 'text-yellow-400'}`}>
                {e.daysLeft ?? '—'}
              </td>
              <td className={`${tdCls} text-[#52525B] text-xs whitespace-nowrap`}>
                {e.expiresAt ? new Date(e.expiresAt as any).toLocaleDateString() : '—'}
              </td>
            </tr>
          ))}
        </AdminTable>
      </section>

      {/* ── Video views ───────────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#71717A] uppercase tracking-widest">
            Video views by section
          </h3>
          <button
            onClick={() => exportCsv(videos, 'mrpanadol_video_views')}
            className="text-xs text-[#A1A1AA] hover:text-yellow-400 transition-colors"
          >Export CSV</button>
        </div>
        <AdminTable
          headers={['Course', 'Section', 'Unique viewers', 'Total opens']}
          empty={videos.length === 0}
        >
          {videos.map(v => (
            <tr key={`${v.courseId}-${v.sectionId}`} className="border-t border-white/[0.04]">
              <td className={`${tdCls} text-white text-sm font-semibold`}>{v.courseTitle}</td>
              <td className={`${tdCls} text-[#E4E4E7] text-sm`}>{v.sectionName}</td>
              <td className={`${tdCls} text-yellow-400 text-sm font-bold`}>{v.viewers}</td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>{v.totalOpens}</td>
            </tr>
          ))}
        </AdminTable>
      </section>
    </div>
  );
}
